import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { Map, useMap, useMapsLibrary, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";

import { useRouter } from "next/router";

import { useTranslation } from "next-i18next";

import { Icon } from "common/ui/Icon";
import { IListingLookupDto, ListingType } from "common/middleware/api.services";
import { currencyFormatLocale, formatSquareRoot, sqMetersToSqFeet } from "common/utils/utils";
import { toEnumValue } from "common/utils/toEnumValue";
import styles from "./DirectoryMap.module.scss";

function formatCompactPrice(price: number | undefined): string {
  if (price == null) return "";
  if (price >= 1_000_000) {
    const m = price / 1_000_000;
    return m % 1 === 0 ? `${m}M` : `${m.toFixed(2).replace(/0+$/, "")}M`;
  }
  if (price >= 1_000) {
    const k = Math.round(price / 1_000);
    return `${k}K`;
  }
  return `${price}`;
}

interface MarkerGroup {
  key: string;
  lat: number;
  lng: number;
  listings: IListingLookupDto[];
}

function groupByLocation(listings: IListingLookupDto[]): MarkerGroup[] {
  const groups: Record<string, MarkerGroup> = {};

  for (const listing of listings) {
    if (listing.latitude == null || listing.longitude == null) continue;

    // Round to 4 decimal places (~11m) to group nearby listings
    const lat = Math.round(listing.latitude * 10000) / 10000;
    const lng = Math.round(listing.longitude * 10000) / 10000;
    const key = `${lat},${lng}`;

    if (groups[key]) {
      groups[key].listings.push(listing);
    } else {
      groups[key] = { key, lat, lng, listings: [listing] };
    }
  }

  return Object.values(groups);
}

interface BoundingBox {
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
}

interface Props {
  listings: IListingLookupDto[];
  searchString?: string;
  onBoundsChange: (bounds: BoundingBox) => void;
  onMarkerClick: (listingId: number) => void;
  onMakeOffer?: (listingId: number) => void;
  onChat?: (listingId: number) => void;
}

function DirectoryMap({
  listings,
  searchString,
  onBoundsChange,
  onMarkerClick,
  onMakeOffer,
  onChat,
}: Props) {
  const { locale } = useRouter();
  const { t } = useTranslation("common");
  const map = useMap();
  const geocodingLibrary = useMapsLibrary("geocoding");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const geocodeDebounceRef = useRef<ReturnType<typeof setTimeout>>();
  const hasFitBounds = useRef(false);
  const lastGeocodedSearch = useRef<string>();
  const skipNextBoundsUpdate = useRef(false);

  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSelected, setIsSelected] = useState(false);

  const markerGroups = useMemo(() => groupByLocation(listings ?? []), [listings]);

  const activeGroup = activeGroupKey
    ? markerGroups.find((g) => g.key === activeGroupKey) ?? null
    : null;
  const activeListing = activeGroup?.listings[activeIndex] ?? null;
  // ListingLookupDto.propertyType maps from the same Listing.ListingType enum as ListingDto.listingType; DTO field names differ but values are identical.
  const isActiveListingLand = toEnumValue(ListingType, activeListing?.propertyType) === ListingType.Land;

  const handleMarkerEnter = useCallback((group: MarkerGroup) => {
    if (isSelected) return;
    setActiveGroupKey(group.key);
    setActiveIndex(0);
  }, [isSelected]);

  const handleMarkerLeave = useCallback(() => {
    if (isSelected) return;
    setActiveGroupKey(null);
    setActiveIndex(0);
  }, [isSelected]);

  const handleMarkerClick = useCallback((group: MarkerGroup) => {
    if (isSelected && activeGroupKey === group.key) {
      // Second click on same group — navigate to detail
      if (activeListing) {
        onMarkerClick(activeListing.id);
      }
    } else {
      setActiveGroupKey(group.key);
      setActiveIndex(0);
      setIsSelected(true);
    }
  }, [isSelected, activeGroupKey, activeListing, onMarkerClick]);

  const handleInfoClose = useCallback(() => {
    setActiveGroupKey(null);
    setActiveIndex(0);
    setIsSelected(false);
  }, []);

  // Fit bounds on initial load when listings arrive
  useEffect(() => {
    if (!map || !listings?.length || hasFitBounds.current) return;

    const bounds = new google.maps.LatLngBounds();
    let hasValidCoords = false;

    listings.forEach((l) => {
      if (l.latitude != null && l.longitude != null) {
        bounds.extend({ lat: l.latitude, lng: l.longitude });
        hasValidCoords = true;
      }
    });

    if (hasValidCoords) {
      map.fitBounds(bounds, 50);
      hasFitBounds.current = true;
    }
  }, [map, listings]);

  // Geocode search string and pan map to result
  useEffect(() => {
    if (!map || !geocodingLibrary || !searchString) return;
    if (searchString === lastGeocodedSearch.current) return;

    if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);

    geocodeDebounceRef.current = setTimeout(() => {
      const geocoder = new geocodingLibrary.Geocoder();
      geocoder.geocode({ address: searchString }, (results, status) => {
        if (status === "OK" && results?.[0]) {
          const viewport = results[0].geometry.viewport;
          skipNextBoundsUpdate.current = true;
          if (viewport) {
            map.fitBounds(viewport);
          } else {
            const location = results[0].geometry.location;
            map.panTo({ lat: location.lat(), lng: location.lng() });
            map.setZoom(12);
          }
          lastGeocodedSearch.current = searchString;
        }
      });
    }, 800);

    return () => {
      if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);
    };
  }, [map, geocodingLibrary, searchString]);

  // Report bounds on idle (debounced 500ms)
  const handleIdle = useCallback(() => {
    if (!map) return;

    if (skipNextBoundsUpdate.current) {
      skipNextBoundsUpdate.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      const bounds = map.getBounds();
      if (!bounds) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      onBoundsChange({
        minLatitude: sw.lat(),
        maxLatitude: ne.lat(),
        minLongitude: sw.lng(),
        maxLongitude: ne.lng(),
      });
    }, 500);
  }, [map, onBoundsChange]);

  return (
    <div className={styles.MapContainer}>
      <Map
        defaultZoom={10}
        defaultCenter={{ lat: 45.5, lng: -73.6 }}
        onIdle={handleIdle}
        onClick={handleInfoClose}
        mapId="directory-map"
        reuseMaps
      >
        {markerGroups.map((group) => {
          const isActive = activeGroupKey === group.key;
          const isSingle = group.listings.length === 1;

          return (
            <AdvancedMarker
              key={group.key}
              position={{ lat: group.lat, lng: group.lng }}
              onClick={() => handleMarkerClick(group)}
            >
              <div
                className={`${styles.PriceMarker} ${isActive ? styles.active : ""}`}
                onMouseEnter={() => handleMarkerEnter(group)}
                onMouseLeave={handleMarkerLeave}
              >
                {isSingle
                  ? formatCompactPrice(group.listings[0].price)
                  : group.listings.length
                }
              </div>
            </AdvancedMarker>
          );
        })}

        {activeListing && activeGroup && (
          <InfoWindow
            position={{ lat: activeGroup.lat, lng: activeGroup.lng }}
            pixelOffset={[0, -30]}
            onCloseClick={handleInfoClose}
          >
            <div className={styles.InfoCard}>
              {activeListing.displayImage?.url ? (
                <img
                  className={styles.InfoImage}
                  src={activeListing.displayImage.url}
                  alt={activeListing.name || activeListing.address?.street || ""}
                />
              ) : (
                <div className={styles.InfoImagePlaceholder}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="M21 15l-5-5L5 21" />
                  </svg>
                </div>
              )}
              <div className={styles.InfoContent}>
                <div className={styles.InfoPrice}>
                  {currencyFormatLocale(activeListing.price, locale)}
                </div>
                {activeListing.address && (
                  <div className={styles.InfoAddress}>
                    {activeListing.address.street}
                    {activeListing.address.city && `, ${activeListing.address.city}`}
                  </div>
                )}
                <div className={styles.InfoDetails}>
                  {!isActiveListingLand && activeListing.bedroomQuantity != null && (
                    <span className={styles.InfoDetail}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7v11a2 2 0 002 2h14a2 2 0 002-2V7" />
                        <path d="M21 13H3" />
                        <path d="M7 7v0a2 2 0 012-2h6a2 2 0 012 2v0" />
                      </svg>
                      {activeListing.bedroomQuantity}
                    </span>
                  )}
                  {!isActiveListingLand && activeListing.bathroomQuantity != null && (
                    <span className={styles.InfoDetail}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12h16a1 1 0 011 1v3a4 4 0 01-4 4H7a4 4 0 01-4-4v-3a1 1 0 011-1z" />
                        <path d="M6 12V5a2 2 0 012-2h1" />
                      </svg>
                      {activeListing.bathroomQuantity}
                    </span>
                  )}
                  {isActiveListingLand ? (
                    activeListing.totalListingAreaInSqMeters != null && (
                      <span>
                        {formatSquareRoot(sqMetersToSqFeet(activeListing.totalListingAreaInSqMeters))} {t("listingCard.lotSizeSqft")}
                      </span>
                    )
                  ) : (
                    activeListing.indoorSquareFeet != null && (
                      <span>{Math.round(activeListing.indoorSquareFeet).toLocaleString()} {t("listingCard.sqft")}</span>
                    )
                  )}
                </div>
                {activeListing.propertyType != null && (
                  <div className={styles.InfoType}>
                    {t(`propertyInfoInput.${activeListing.propertyType}`, { defaultValue: "" })}
                  </div>
                )}
                {(onMakeOffer || onChat) && (
                  <div className={styles.InfoActions}>
                    {onMakeOffer && (
                      <button
                        className={styles.InfoActionBtn}
                        onClick={() => onMakeOffer(activeListing.id!)}
                      >
                        <Icon icon="currencyDollar" />
                        {t("listingCard.makeOffer")}
                      </button>
                    )}
                    {onChat && (
                      <button
                        className={styles.InfoActionBtn}
                        onClick={() => onChat(activeListing.id!)}
                      >
                        <Icon icon="chatBubble" />
                        {t("listingCard.chat")}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {activeGroup.listings.length > 1 && (
                <div className={styles.InfoPaging}>
                  <button
                    className={styles.InfoPageBtn}
                    disabled={activeIndex === 0}
                    onClick={() => setActiveIndex((i) => i - 1)}
                  >
                    &lsaquo;
                  </button>
                  <span className={styles.InfoPageLabel}>
                    {activeIndex + 1} / {activeGroup.listings.length}
                  </span>
                  <button
                    className={styles.InfoPageBtn}
                    disabled={activeIndex === activeGroup.listings.length - 1}
                    onClick={() => setActiveIndex((i) => i + 1)}
                  >
                    &rsaquo;
                  </button>
                </div>
              )}
            </div>
          </InfoWindow>
        )}
      </Map>
    </div>
  );
}

export { DirectoryMap };
